import React, { useEffect } from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import ContactSection from "../ContactSection";
import Lenis from "@studio-freight/lenis";
import {
  ShoppingCart,
  Users,
  FileSpreadsheet,
  FileCheck,
  TrendingDown,
  Clock,
  ArrowRight
} from "lucide-react";
import "../styles/website.css";
import "../styles/ProductPages.css";

const PurchaseProcurement = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      smoothWheel: true,
      smoothTouch: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const features = [
    {
      title: "Supplier Management",
      description: "Keep a centralized vendor directory with ratings, contacts, and contract terms.",
      icon: <Users size={22} />
    },
    {
      title: "Purchase Requisitions",
      description: "Streamline department requests and automate approval routing protocols.",
      icon: <FileSpreadsheet size={22} />
    },
    {
      title: "Purchase Orders (POs)",
      description: "Auto-generate professional PO PDFs and email them directly to suppliers.",
      icon: <ShoppingCart size={22} />
    },
    {
      title: "RFQ & Bidding Analysis",
      description: "Upload supplier quotes, compare bids side-by-side, and secure best prices.",
      icon: <TrendingDown size={22} />
    },
    {
      title: "Goods Receipt Note (GRN)",
      description: "Match shipments with POs, verify quantity/quality, and track backorders.",
      icon: <FileCheck size={22} />
    },
    {
      title: "Procurement Audits",
      description: "Review purchase volumes, vendor performance logs, and lead-time schedules.",
      icon: <Clock size={22} />
    }
  ];

  return (
    <div className="prod-page-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="prod-hero-section">
        <div className="prod-hero-glow"></div>
        <div className="prod-hero-left">
          <span className="prod-badge">PURCHASE & PROCUREMENT</span>
          <h1 className="prod-main-title">
            Smart Purchases. <br />
            <span>Efficient Cycles.</span>
          </h1>
          <p className="prod-desc">
            Organize supplier catalogs, coordinate purchase requisitions, generate official POs,
            verify incoming goods, and audit procurement expenses.
          </p>
          <div className="prod-cta-group">
            <a href="#contact" className="prod-primary-btn">
              Request Demo <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* HERO VISUAL MOCKUP */}
        <div className="prod-hero-right">
          <div className="prod-mockup-frame">
            <div className="mockup-header">
              <div className="mockup-dots">
                <div className="mockup-dot red"></div>
                <div className="mockup-dot yellow"></div>
                <div className="mockup-dot green"></div>
              </div>
              <span className="mockup-title">procurement.gtelinq.com/dashboard</span>
            </div>
            <div className="mockup-content">
              <div className="mockup-sidebar">
                <div className="sidebar-item"></div>
                <div className="sidebar-item active"></div>
                <div className="sidebar-item"></div>
                <div className="sidebar-item"></div>
              </div>
              <div className="mockup-main">
                <div className="mockup-bar"></div>
                <div className="mockup-cards">
                  <div className="mockup-card">
                    <h5>Active POs</h5>
                    <span className="mockup-card-value">24</span>
                    <div className="mockup-card-metric">
                      <div className="mockup-card-fill" style={{ width: "70%", background: "#55FF60" }}></div>
                    </div>
                  </div>
                  <div className="mockup-card">
                    <h5>Pending Approvals</h5>
                    <span className="mockup-card-value">3</span>
                    <div className="mockup-card-metric">
                      <div className="mockup-card-fill" style={{ width: "15%", background: "#ffbd2e" }}></div>
                    </div>
                  </div>
                </div>
                <div className="mockup-chart-container">
                  <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.4)" }}>MONTHLY PROCUREMENT SPEND</span>
                  <div className="chart-bars">
                    <div className="chart-bar" style={{ height: "35px" }}></div>
                    <div className="chart-bar" style={{ height: "45px" }}></div>
                    <div className="chart-bar highlight" style={{ height: "60px" }}></div>
                    <div className="chart-bar" style={{ height: "50px" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="prod-features-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">PROCUREMENT UTILITIES</span>
          <h2 className="prod-section-title">Automate Requisitions and Audits</h2>
        </div>
        <div className="prod-features-grid">
          {features.map((feat, idx) => (
            <div className="prod-feature-card" key={idx}>
              <div className="prod-feature-icon">{feat.icon}</div>
              <h4>{feat.title}</h4>
              <p>{feat.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="prod-testimonials-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">TESTIMONIALS</span>
          <h2 className="prod-section-title">Purchasing Success Stories</h2>
        </div>
        <div className="prod-testimonials-grid">
          <div className="prod-testimonial-card">
            <p className="prod-quote">
              "We simplified our requisition workflows. GTE Procurement routed requests automatically
              for manager approvals, speeding up our purchase cycles by 60%."
            </p>
            <div className="prod-author-info">
              <div className="prod-author-avatar">RD</div>
              <div className="prod-author-meta">
                <h5>Ramesh Deshpande</h5>
                <span>Facilities Manager, Nexus Corp</span>
              </div>
            </div>
          </div>
          <div className="prod-testimonial-card">
            <p className="prod-quote">
              "Comparing vendor quotations side-by-side helps us get the best prices. Matching goods receipts
              with original POs prevents vendor invoice errors."
            </p>
            <div className="prod-author-info">
              <div className="prod-author-avatar">AR</div>
              <div className="prod-author-meta">
                <h5>Alisha Rao</h5>
                <span>Procurement Lead, PrimeSpaces Pvt Ltd</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="prod-faq-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">FAQ</span>
          <h2 className="prod-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="prod-faq-list">
          <div className="prod-faq-item">
            <h4>Can we track partial shipments?</h4>
            <p>Yes, the system logs goods received partially and leaves remaining balances in active, open PO states.</p>
          </div>
          <div className="prod-faq-item">
            <h4>Does it support limit-based approval routing?</h4>
            <p>Yes, you can establish custom rules so requisitions exceeding budget limits require secondary approval stages.</p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <div className="con-page-wrapper">
        <ContactSection />
        <Footer />
      </div>
    </div>
  );
};

export default PurchaseProcurement;
